import React, { useState, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  alpha,
} from '@mui/material';
import { Lock as LockIcon } from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { ClientField, ClientFieldBase } from 'shared/types/appTypes';
import FormQuestion from './FormQuestion';
import FormNavigation from './FormNavigation';
import FormProgress from './FormProgress';
import { formatTimeTzNaive, formatDateValueTzNaive } from 'src/utils/formatDateTzNaive';

interface FormContentProps {
  fields: ClientField[];
  onSave: (fieldIndex: number) => Promise<void>;
  onComplete?: () => void;
  getFieldValue: (fieldIndex: number) => any;
  setFieldValue: (fieldIndex: number, value: any) => void;
  isSaving?: boolean;
  onDirtyChange?: (isDirty: boolean) => void;
  initialIndex?: number;
}

const FormContent: React.FC<FormContentProps> = ({ 
  fields, 
  onSave, 
  onComplete,
  getFieldValue,
  setFieldValue,
  isSaving = false,
  onDirtyChange,
  initialIndex = 0,
}) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [slideDirection, setSlideDirection] = useState<number>(-100);
  const [isDirty, setIsDirty] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<(() => void) | null>(null);
  const [showingResponse, setShowingResponse] = useState(false);
  const savedValueRef = useRef<any>(null);

  const currentField = fields[currentIndex];
  const isFirstQuestion = currentIndex === 0;
  const isLastQuestion = currentIndex === fields.length - 1;
  const isFullWidthField = currentField?.type === 'video' || currentField?.type === 'appleMusicPlaylist';
  const currentValue = getFieldValue(currentIndex);
  const isAutoPopulated = !!(
    currentField &&
    'autoPopulatedValue' in currentField &&
    (currentField as any).autoPopulatedValue !== undefined &&
    JSON.stringify(currentValue) === JSON.stringify((currentField as any).autoPopulatedValue)
  );
  const isFinalized = currentField?.type !== 'video' && 'finalized' in currentField && !!currentField.finalized;

  /** Convert a field value to human-readable text for [value] interpolation */
  const formatValueForDisplay = useCallback((field: ClientField, value: any): string => {
    if (value == null) return '';
    switch (field.type) {
      case 'shortText':
      case 'longText':
        return String(value);
      case 'number':
        return value === '' ? '' : String(value);
      case 'boolean':
        return value === true ? 'Yes' : value === false ? 'No' : '';
      case 'address': {
        const address = value || {};
        const line1 = String(address.addressLine1 || '').trim();
        const line2 = String(address.addressLine2 || '').trim();
        const city = String(address.city || '').trim();
        const state = String(address.state || '').trim();
        const postalCode = String(address.postalCode || '').trim();
        const country = String(address.country || '').trim();

        const cityLine = [city, state, postalCode].filter(Boolean).join(', ');
        return [line1, line2, cityLine, country].filter(Boolean).join(', ');
      }
      case 'date':
        return formatDateValueTzNaive(value);
      case 'time':
        return formatTimeTzNaive(value);
      case 'dateRange': {
        const startDate = formatDateValueTzNaive(value?.start);
        const endDate = formatDateValueTzNaive(value?.end);
        return [startDate, endDate].filter(Boolean).join(' – ');
      }
      case 'timeRange': {
        const startTime = formatTimeTzNaive(value?.start);
        const endTime = formatTimeTzNaive(value?.end);
        return [startTime, endTime].filter(Boolean).join(' – ');
      }
      case 'multipleChoice':
        if (Array.isArray(value)) {
          return value.filter((item) => String(item || '').trim().length > 0).join(', ');
        }
        return String(value);
      case 'imageChoice':
        if ('options' in field && field.options) {
          const match = field.options.find((o) => o.label === value);
          return match?.label ?? String(value);
        }
        return String(value);
      case 'appleMusicPlaylist':
        if (Array.isArray(value)) return `${value.length} song${value.length === 1 ? '' : 's'} selected`;
        return String(value);
      default:
        return String(value);
    }
  }, []);

  /** Interpolate [value] tokens in customResponseText */
  const interpolateResponseText = useCallback((text: string, field: ClientField, value: any): string => {
    return text.replace(/\[value\]/gi, formatValueForDisplay(field, value));
  }, [formatValueForDisplay]);

  // Track initial value when index changes
  React.useEffect(() => {
    savedValueRef.current = getFieldValue(currentIndex);
    setIsDirty(false);
    onDirtyChange?.(false);
    setShowingResponse(false);
  }, [currentIndex]); // Only reset when index changes, not when getFieldValue changes

  const handleValueChange = useCallback((value: any) => {
    setFieldValue(currentIndex, value);
    // Mark as dirty if value differs from saved value
    const savedValue = savedValueRef.current;
    const hasChanged = JSON.stringify(value) !== JSON.stringify(savedValue);
    setIsDirty(hasChanged);
    onDirtyChange?.(hasChanged);
  }, [currentIndex, setFieldValue, onDirtyChange]);

  const handleNext = useCallback(async () => {
    // If we're showing a response screen, advance to next question
    if (showingResponse) {
      setShowingResponse(false);
      if (isLastQuestion) {
        onComplete?.();
        return;
      }
      setSlideDirection(-100);
      setCurrentIndex((prev) => prev + 1);
      return;
    }

    try {
      await onSave(currentIndex);
      savedValueRef.current = getFieldValue(currentIndex);
      setIsDirty(false);
      onDirtyChange?.(false);

      // Check if the field has customResponseText to show
      const field = fields[currentIndex];
      const hasCustomResponse = field.type !== 'video' && 'customResponseText' in field && field.customResponseText;
      if (hasCustomResponse) {
        setShowingResponse(true);
        return;
      }
      
      if (isLastQuestion) {
        onComplete?.();
        return;
      }
      
      setSlideDirection(-100);
      setCurrentIndex((prev) => prev + 1);
    } catch (error) {
      console.error('Error saving field:', error);
      // Re-throw to let parent handle
      throw error;
    }
  }, [currentIndex, onSave, isLastQuestion, onComplete, getFieldValue, onDirtyChange, showingResponse, fields]);

  const handleSkip = useCallback(() => {
    const navigateAway = () => {
      setShowingResponse(false);
      if (isLastQuestion) {
        onComplete?.();
        return;
      }
      
      setSlideDirection(-100);
      setCurrentIndex((prev) => prev + 1);
    };

    if (showingResponse) {
      navigateAway();
      return;
    }

    if (isDirty) {
      setPendingNavigation(() => navigateAway);
    } else {
      navigateAway();
    }
  }, [isLastQuestion, onComplete, isDirty, showingResponse]);

  const handleBack = useCallback(() => {
    // If showing response, go back to the question
    if (showingResponse) {
      setShowingResponse(false);
      return;
    }

    if (isFirstQuestion) return;

    const navigateAway = () => {
      setSlideDirection(100);
      setCurrentIndex((prev) => prev - 1);
    };

    if (isDirty) {
      setPendingNavigation(() => navigateAway);
    } else {
      navigateAway();
    }
  }, [isFirstQuestion, isDirty, showingResponse]);

  const handleNavigate = useCallback((index: number) => {
    if (index === currentIndex && !showingResponse) return;

    const navigateAway = () => {
      setShowingResponse(false);
      setSlideDirection(index > currentIndex ? -100 : 100);
      setCurrentIndex(index);
    };

    if (isDirty) {
      setPendingNavigation(() => navigateAway);
    } else {
      navigateAway();
    }
  }, [currentIndex, isDirty, showingResponse]);

  const handleConfirmDiscard = () => {
    if (pendingNavigation) {
      // Reset field to saved value
      setFieldValue(currentIndex, savedValueRef.current);
      setIsDirty(false);
      onDirtyChange?.(false);
      pendingNavigation();
      setPendingNavigation(null);
    }
  };

  const handleCancelDiscard = () => {
    setPendingNavigation(null);
  };

  if (!currentField) {
    return null;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
      }}
    >
      {/* Question area - biased to top 1/3, scrollable */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start',
          alignItems: 'center',
          pt: { xs: '10vh', sm: '15vh' },
          px: isFullWidthField ? 0 : { xs: 2, sm: 3 },
          py: { xs: 4, sm: 6 },
        }}
      >
        <AnimatePresence mode="wait" custom={slideDirection}>
          <motion.div
            key={showingResponse ? `response-${currentIndex}` : currentIndex}
            custom={slideDirection}
            variants={{
              initial: { opacity: 0 },
              animate: { opacity: 1, transition: { duration: 0.4, ease: 'easeOut' } },
              exit: (custom: number) => ({
                opacity: 0,
                x: custom,
                transition: { duration: 0.25, ease: 'easeIn' }
              })
            }}
            initial="initial"
            animate="animate"
            exit="exit"
            style={{ width: '100%', maxWidth: isFullWidthField ? 'none' : 600, textAlign: 'center' }}
          >
            {showingResponse && currentField.type !== 'video' && 'customResponseText' in currentField && currentField.customResponseText ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, width: '100%' }}>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 500,
                    lineHeight: 1.3,
                    fontSize: { xs: '1.25rem', sm: '1.5rem' },
                  }}
                >
                  {interpolateResponseText(currentField.customResponseText, currentField, getFieldValue(currentIndex))}
                </Typography>
                <Button
                  variant="contained"
                  onClick={handleNext}
                  size="small"
                >
                  {(currentField as ClientFieldBase).customResponseButtonText || 'Ok, next question'}
                </Button>
              </Box>
            ) : (
              <Box sx={{ width: '100%' }}>
                <FormQuestion
                  field={currentField}
                  questionNumber={currentIndex + 1}
                  value={getFieldValue(currentIndex)}
                  onChange={handleValueChange}
                  disabled={isFinalized}
                />
                {isFinalized && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 0.75,
                      mt: 2,
                      py: 1,
                      px: 2,
                      borderRadius: 1,
                      bgcolor: (theme) => alpha(theme.palette.info.main, 0.06),
                    }}
                  >
                    <LockIcon sx={{ fontSize: 14, color: 'info.main', opacity: 0.7 }} />
                    <Typography variant="caption" sx={{ color: 'info.main', fontWeight: 500 }}>
                      This response has been finalized
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </motion.div>
        </AnimatePresence>
      </Box>

      {/* Progress bar */}
      <FormProgress
        current={currentIndex + 1}
        total={fields.length}
        fields={fields}
        getFieldValue={getFieldValue}
        onNavigate={handleNavigate}
      />

      {/* Navigation */}
      <FormNavigation
        onNext={handleNext}
        onSkip={handleSkip}
        onBack={handleBack}
        isFirstQuestion={isFirstQuestion && !showingResponse}
        isLastQuestion={isLastQuestion}
        hasValue={getFieldValue(currentIndex) != null && getFieldValue(currentIndex) !== ''}
        isSaving={isSaving}
        currentField={currentField}
        showingResponse={showingResponse}
        isFinalized={isFinalized}
        isAutoPopulated={isAutoPopulated}
      />

      {/* Unsaved changes confirmation dialog */}
      <Dialog
        open={!!pendingNavigation}
        onClose={handleCancelDiscard}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Continue without saving?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You have unsaved changes on this question. If you continue, your changes will be lost.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDiscard} variant="outlined" color="inherit" >
            Cancel
          </Button>
          <Button onClick={handleConfirmDiscard} variant="contained" color="warning">
            Continue
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FormContent;
